import { format } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import { DateRange } from "react-date-range";
import { useNavigate } from "react-router";
import { useLocation, useParams } from "react-router-dom";
import StackButton from '@/components/buttons/StackButton';
import NoDataAvailable from "@/components/cards/NoDataAvailable";
import ContainerWithHeader from "@/components/layouts/ContainerWithHeader";
import { LinesStackSkeleton } from "@/components/skeletons";
import SearchBar from "@/components/navbars/SearchBar";
import SharedAccessUserTable from "@/components/tables/SharedAccessUserTable";
import { createSharedAccess, fetchUsers } from "@/services/user.service";
import { SHARED_ACCESS } from "@/utils/constants/appConstants/SharedAcessConstant";
import { BUTTONS } from '@/utils/constants/appConstants/commonConstants';
import { COLORS } from "@/utils/constants/colors";
import { NO_USER_YET } from "@/utils/constants/messages";
import { getName, getUserId } from "@/utils/functions/TokenFunction";
import { concatenateName } from "@/utils/functions/helperFunctions";
import { errorToasterMessage, successToasterMessage } from "@/utils/functions/toaster";
import { useDataQueries } from "@/utils/hooks/useDataQueries";

const CreateSharedAccess = () => {
    const [open, setOpen] = useState(false);
    const [dateSelection, setDateSelection] = useState<any>({
        start_date: undefined,
        end_date: undefined,
        key: "selection",
    });
    const [availableUsers, setAvailableUsers] = useState<any>([]);
    const [selectedUsers, setSelectedUsers] = useState<any>([]);
    const [accessData, setAccessData] = useState<any>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [dataLoad, setDataLoad] = useState<boolean>(false);
    const [usersLeft, setUsersLeft] = useState<boolean>(true);
    const [allFetched, setAllFetched] = useState(false);
    const [showSearchBar, setShowSearchBar] = useState(false);
    const [saveDelegatesLoader, setSaveDelegatesloader] = useState(false);
    const [activeUserTab, setActiveUserTab] = useState<"available" | "selected">("available");
    const [pageNo, setPageNo, dataLength, setDataLength, sorting, setSorting, filter, setFilter, searchItem, setSearchItem, resetDataQueries] = useDataQueries();
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as any;
    const params = useParams();
    const user_id = getUserId();
    const containerRef = useRef<HTMLDivElement>(null);

    const handleSearch = (searchValue: string) => setSearchItem(searchValue);

    const handleFetchUsers = () => {
        setFilter({ ...filter, is_active: true, is_verified: true });
        fetchUsers(
            pageNo,
            dataLength,
            sorting,
            { is_active: true, is_verified: true },
            searchItem,
            (response: any) => {
                setIsLoading(false);
                setDataLoad(false);
                if (response.data?.users?.length < dataLength) {
                    setUsersLeft(false);
                    setAllFetched(true);
                }
                setAvailableUsers(response.data.users);
            },
            (error: any) => {
                console.log(error);
                setIsLoading(false);
                setDataLoad(false);
            }
        );
    };

    useEffect(() => {
        setAvailableUsers([]);
        setPageNo(1);
        setUsersLeft(true);
        setAllFetched(false);
        setIsLoading(true);
        setDataLoad(true);
        handleFetchUsers();
    }, [dataLength, sorting, searchItem]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const handleScroll = () => {
            if (allFetched || isLoading) return;
            if (container.scrollTop + container.clientHeight >= container.scrollHeight - 1) {
                setPageNo(pageNo + 1);
                setIsLoading(true);
                if (usersLeft) {
                    fetchUsers(
                        pageNo + 1,
                        dataLength,
                        sorting,
                        filter,
                        searchItem,
                        (response: any) => {
                            setIsLoading(false);
                            if (response.data?.users?.length < dataLength) {
                                setUsersLeft(false);
                                setAllFetched(true);
                            }
                            setAvailableUsers([...availableUsers, ...response.data.users]);
                        },
                        (error: any) => {
                            console.log(error);
                            setIsLoading(false);
                        }
                    );
                }
            }
        };
        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [allFetched, isLoading, usersLeft, dataLength, searchItem]);

    const handleToggleUserSelection = (user: any) => {
        const isSelected = selectedUsers.some((u: any) => u.id === user.id);
        if (isSelected) {
            setSelectedUsers(selectedUsers.filter((u: any) => u.id !== user.id));
        } else {
            setSelectedUsers([...selectedUsers, user]);
        }
    };

    const handleClearDate = () => {
        setDateSelection({ startDate: undefined, endDate: undefined, key: "selection" });
    };

    const handlePermissionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAccessData({ ...accessData, permissions: e.target.value });
    };

    const handleDateSelection = (ranges: any) => {
        const { selection } = ranges;
        setDateSelection(selection);
        const formattedStartDate = format(new Date(selection.startDate), "yyyy-MM-dd");
        const formattedEndDate = format(new Date(selection.endDate), "yyyy-MM-dd");
        setAccessData({ ...accessData, start_date: formattedStartDate, end_date: formattedEndDate });
    };

    const handleCreateSharedAccess = () => {
        if (!accessData) { errorToasterMessage(SHARED_ACCESS.PERMISSION_FIELD.REQUIRED); return; }
        if (selectedUsers.length === 0) { errorToasterMessage(SHARED_ACCESS.USER_REQUIRED); return; }
        setSaveDelegatesloader(true);
        const data = {
            ...accessData,
            assigned_by: user_id,
            delegator: params.id,
            users: selectedUsers.map((user: any) => user.id).flat(),
        };
        createSharedAccess(data)
            .then((res: any) => {
                setSaveDelegatesloader(false);
                successToasterMessage(`${concatenateName(state.user.first_name, state.user.last_name)} shared access with ${selectedUsers.length} users`);
                navigate(`/shared-access/${params.id}`, { replace: true, state: { user: state.user } });
            })
            .catch((err: any) => {
                setSaveDelegatesloader(false);
                errorToasterMessage(err.response.data.message);
            });
    };

    return (
        <ContainerWithHeader heading={SHARED_ACCESS.SHARE_ACCESS_WITH_OTHERS} subText={``}>
            <div className="space-y-4">
                <div className="gs-list-page-card px-5 py-4">
                    <p className="font-semibold mb-3">{SHARED_ACCESS.PERMISSION_FIELD.TEXT}</p>
                    <div className="space-y-3">
                        {[
                            { value: "manage", label: SHARED_ACCESS.PERMISSION_FIELD.OPTION_1, desc: SHARED_ACCESS.PERMISSION_FIELD.OPTION_1_PLACEHOLDER },
                            { value: "edit", label: SHARED_ACCESS.PERMISSION_FIELD.OPTION_2, desc: SHARED_ACCESS.PERMISSION_FIELD.OPTION_2_PLACEHOLDER },
                            { value: "send", label: SHARED_ACCESS.PERMISSION_FIELD.OPTION_3, desc: SHARED_ACCESS.PERMISSION_FIELD.OPTION_3_PLACEHOLDER },
                        ].map(({ value, label, desc }) => (
                            <div key={value} className="flex items-start gap-2">
                                <input type="radio" name="permissions" id={value} value={value} onChange={handlePermissionChange} className="mt-1" />
                                <div>
                                    <label htmlFor={value} className="font-semibold m-0 cursor-pointer">{label}</label>
                                    <p className="text-sm text-muted-foreground">{desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="gs-list-page-card px-5 py-4">
                    <p className="font-semibold mb-3">{SHARED_ACCESS.DURATION_FIELD}</p>
                    <div className="flex items-center gap-2 border border-muted rounded px-3 py-2 mb-2 w-fit">
                        <input
                            placeholder="Date Range"
                            value={dateSelection.startDate && dateSelection.endDate
                                ? `${new Date(dateSelection.startDate as string).toDateString()} - ${new Date(dateSelection.endDate as string).toDateString()}`
                                : ""}
                            className="border-0 outline-none text-sm text-card-foreground min-w-[200px]"
                            onClick={() => setOpen(!open)}
                            readOnly
                        />
                        <em className="icon ni ni-cross cursor-pointer" onClick={handleClearDate} />
                    </div>
                    {open && <DateRange className="w-full my-custom-date-range" ranges={[dateSelection]} onChange={handleDateSelection} rangeColors={[COLORS.primary, COLORS.primary, COLORS.primary]} minDate={new Date()} />}
                </div>

                <div className="gs-list-page-card px-5 py-4">
                    <p className="font-semibold">{SHARED_ACCESS.USERS_FIELD}</p>
                    <p className="text-sm text-muted-foreground mb-3">{SHARED_ACCESS.USERS_SUB_TEXT}</p>
                    <hr className="border-muted mb-3" />
                    <ul className="gs-page-tabs">
                        <li>
                            <button
                                className={`gs-page-tab${activeUserTab === "available" ? " gs-page-tab--active" : ""}`}
                                id="role-detail-permission-tab"
                                onClick={() => setActiveUserTab("available")}
                            >
                                {`${SHARED_ACCESS.AVAILABLE_USERS} (${availableUsers.length})`}
                            </button>
                        </li>
                        <li>
                            <button
                                className={`gs-page-tab${activeUserTab === "selected" ? " gs-page-tab--active" : ""}`}
                                id="role-detail-user-tab"
                                onClick={() => setActiveUserTab("selected")}
                            >
                                {`${SHARED_ACCESS.SELECTED} (${selectedUsers.length})`}
                            </button>
                        </li>
                    </ul>
                    <div>
                        {activeUserTab === "available" && (
                            <>
                                <div className="bg-surface-subtle p-2 mb-2">
                                    {showSearchBar ? (
                                        <SearchBar handleSearch={handleSearch} onHide={() => { setShowSearchBar(false); setSearchItem(""); }} title={""} id={""} />
                                    ) : (
                                        <div className="flex justify-end">
                                            <button className="p-2 rounded hover:bg-background transition-colors" onClick={() => setShowSearchBar(true)}>
                                                <em id="shared-access-search-icon" className="icon ni ni-search" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div ref={containerRef} style={{ maxHeight: "430px", overflowY: "auto" }}>
                                    {!dataLoad ? (
                                        availableUsers.length > 0 ? (
                                            <SharedAccessUserTable users={availableUsers} handleUserToggle={handleToggleUserSelection} selectedUsers={selectedUsers} loadingData={isLoading} />
                                        ) : (
                                            <NoDataAvailable><div className="text-center">{NO_USER_YET}</div></NoDataAvailable>
                                        )
                                    ) : (
                                        <LinesStackSkeleton rowCount={5} className="space-y-2 p-4" />
                                    )}
                                </div>
                            </>
                        )}
                        {activeUserTab === "selected" && (
                            <SharedAccessUserTable users={selectedUsers} handleUserToggle={handleToggleUserSelection} selectedUsers={selectedUsers} />
                        )}
                    </div>
                </div>

                <div className="flex my-3">
                    <StackButton isLoading={saveDelegatesLoader} primaryId='create-shared-access-save-btn' secondaryId='create-shared-access-cancel-btn' primaryButton={SHARED_ACCESS.ADD_ACCESS_SAVE_BUTTON_TEXT} secondaryButton={BUTTONS.CANCEL} handlePrimary={handleCreateSharedAccess} handleSecondary={() => navigate(`/shared-access/${params.id}`, { replace: true, state: { user: state.user } })} />
                </div>
            </div>
        </ContainerWithHeader>
    );
};

export default CreateSharedAccess;
